import { useEffect, useRef } from "react";
import { Check, CircleAlert, CircleCheck, Clock3, Copy, Layers3, Link2, Loader2, MousePointerClick } from "lucide-react";

const syncStyles = {
  synced: { label: "Synced", icon: CircleCheck, classes: "text-emerald-300 bg-emerald-500/10" },
  syncing: { label: "Syncing", icon: Loader2, classes: "text-cyan-300 bg-cyan-500/10" },
  failed: { label: "Failed", icon: CircleAlert, classes: "text-red-300 bg-red-500/10" },
  drift: { label: "Drift", icon: CircleAlert, classes: "text-red-300 bg-red-500/10" },
  pending: { label: "Pending", icon: Clock3, classes: "text-amber-300 bg-amber-500/10" },
};

const CampaignCheckbox = ({ campaign, onToggle, selectedLinks }) => {
  const ref = useRef(null);
  const selectedCount = campaign.variants.filter((variant) => selectedLinks.has(variant.id)).length;
  const checked = selectedCount === campaign.variantCount;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selectedCount > 0 && !checked;
  }, [checked, selectedCount]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={() => onToggle(campaign)}
      className="mt-1 h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 text-cyan-500 focus:ring-cyan-500"
      data-testid={`campaign-checkbox-${campaign.id}`}
      aria-label={`Select ${campaign.name}`}
    />
  );
};

export const CampaignLinksMaster = ({ campaigns, copiedItem, copyToClipboard, getFullUrl, onSelectCampaign, onToggleSelectCampaign, selectedCampaignId, selectedLinks }) => {
  if (campaigns.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-700 bg-slate-900/35 px-6 py-16 text-center" data-testid="links-search-empty-state">
        <Link2 className="mx-auto h-9 w-9 text-slate-600" />
        <p className="mt-3 text-sm font-medium text-slate-300">No campaigns match your search</p>
        <p className="mt-1 text-xs text-slate-500">Try a campaign name, domain, path, or destination.</p>
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-md border border-slate-800 bg-slate-950/45 shadow-xl shadow-cyan-950/10">
      <header className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-white" data-testid="master-pane-title">Campaigns</p>
          <p className="text-xs text-slate-500" data-testid="master-pane-count">{campaigns.length} visible</p>
        </div>
        <MousePointerClick className="h-4 w-4 text-cyan-400" />
      </header>
      <div className="max-h-[66vh] divide-y divide-slate-800/80 overflow-y-auto" data-testid="master-campaigns-list">
        {campaigns.map((campaign) => {
          const selected = selectedCampaignId === campaign.id;
          const firstVariant = campaign.variants[0];
          const copyId = `master-campaign-${campaign.id}`;
          const sync = syncStyles[campaign.syncStatus] || syncStyles.pending;
          const SyncIcon = sync.icon;
          return (
            <article key={campaign.id} className={`group relative flex items-start gap-3 px-3 py-3 transition-colors ${selected ? "bg-cyan-500/10" : "hover:bg-slate-900/80"}`} data-testid={`campaign-item-${campaign.id}`}>
              <span className={`absolute inset-y-0 left-0 w-0.5 ${selected ? "bg-cyan-400" : "bg-transparent group-hover:bg-slate-700"}`} />
              <CampaignCheckbox campaign={campaign} onToggle={onToggleSelectCampaign} selectedLinks={selectedLinks} />
              <button type="button" onClick={() => onSelectCampaign(campaign)} className="min-w-0 flex-1 text-left" data-testid={`campaign-open-${campaign.id}`} aria-pressed={selected}>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <p className="truncate text-sm font-semibold text-slate-100" title={campaign.name} data-testid={`campaign-title-${campaign.id}`}>{campaign.name}</p>
                  <span className="shrink-0 font-mono text-xs text-slate-400" data-testid={`master-campaign-clicks-${campaign.id}`}>{campaign.clicks.toLocaleString()} clicks</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1.5 rounded bg-slate-800 px-2 py-1 text-slate-300" data-testid={`master-campaign-variants-${campaign.id}`}>
                    <Layers3 className="h-3 w-3 text-cyan-400" /> {campaign.domainCount} domains · {campaign.variantCount} variants
                  </span>
                  <span className={`rounded px-2 py-1 ${campaign.activityStatus === "Active" ? "bg-emerald-500/10 text-emerald-300" : campaign.activityStatus === "Mixed" ? "bg-amber-500/10 text-amber-300" : "bg-slate-800 text-slate-400"}`} data-testid={`master-campaign-status-${campaign.id}`}>{campaign.activityStatus}</span>
                  <span className={`inline-flex items-center gap-1 rounded px-2 py-1 ${sync.classes}`} data-testid={`master-campaign-sync-${campaign.id}`}><SyncIcon className={`h-3 w-3 ${campaign.syncStatus === "syncing" ? "animate-spin" : ""}`} />{sync.label}</span>
                </div>
              </button>
              <button type="button" onClick={() => copyToClipboard(getFullUrl(firstVariant), copyId)} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-cyan-500/10 hover:text-cyan-300" data-testid={`master-copy-campaign-${campaign.id}`} aria-label={`Copy first link for ${campaign.name}`} title="Copy first campaign link">
                {copiedItem === copyId ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
};